import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useBlocker } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'

interface ContextualSaveBarProps {
  isDirty: boolean
  isSaving: boolean
  onSave: () => void
  onDiscard: () => void
  /** Increment to trigger shake animation (e.g. on validation error) */
  shake?: number
}

export function ContextualSaveBar(props: ContextualSaveBarProps) {
  if (!props.isDirty) return null
  return <ContextualSaveBarContent {...props} />
}

function ContextualSaveBarContent({
  isDirty,
  isSaving,
  onSave,
  onDiscard,
  shake = 0,
}: ContextualSaveBarProps) {
  const [isShaking, setIsShaking] = useState(false)
  const [blockCount, setBlockCount] = useState(0)
  const [prevShake, setPrevShake] = useState(shake)

  // Detect shake prop changes (React recommended render-time pattern with state)
  if (shake !== prevShake) {
    setPrevShake(shake)
    if (shake > 0) setIsShaking(true)
  }

  // Block navigation + shake when dirty (Shopify-style: no dialog, just blocks and shakes)
  useBlocker({
    shouldBlockFn: () => {
      setIsShaking(true)
      setBlockCount((c) => c + 1)
      return true
    },
    enableBeforeUnload: true,
    disabled: !isDirty,
  })

  return (
    <div className="sticky top-12 z-30 h-0 overflow-visible">
      <div className="mx-auto w-full max-w-3xl px-4 animate-in slide-in-from-top-2 fade-in-0 duration-200">
        <div
          className={`flex items-center justify-between gap-4 rounded-lg bg-foreground px-4 py-2.5 text-background shadow-lg ${
            isShaking ? 'animate-shake' : ''
          }`}
          onAnimationEnd={() => setIsShaking(false)}
        >
          <p className="text-sm font-medium">
            {blockCount >= 5
              ? 'Ok, eu desisto. Mas suas alterações não.'
              : blockCount >= 3
                ? 'Sério, salva logo'
                : blockCount >= 2
                  ? 'Ei, salva primeiro!'
                  : 'Alterações não salvas'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onDiscard}
              disabled={isSaving}
              className="text-background/70 hover:text-background hover:bg-background/10"
            >
              Descartar
            </Button>
            <Button
              size="sm"
              onClick={onSave}
              disabled={isSaving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {isSaving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Salvar
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
